import type { TriageAgentMetadata } from "shared";
import type { AITriageNoteProps, DetailRow } from "./AITriageNote";

// ─── Helpers ────────────────────────────────────────────────────────

const capitalize = (value: string): string =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

/**
 * Statuses that indicate a human took over the AI triage. When the agent
 * did not run to completion we surface the interrupt note in the card.
 */
const MANUAL_STATUSES = new Set(["manual", "overridden", "interrupted"]);

const buildInterruptedNote = (
  meta: TriageAgentMetadata,
): string | null => {
  const status = meta.final_agent_status?.toLowerCase() ?? "";
  if (MANUAL_STATUSES.has(status)) {
    return "Note: AI triaging was interrupted — a team member took control.";
  }
  return null;
};

// ─── Mapper ─────────────────────────────────────────────────────────

/**
 * Transforms the raw triage_agent_metadata payload from a
 * `triage_agent_response` chat message into presentational props for
 * <AITriageNote />. Missing/empty fields degrade gracefully (rows are
 * omitted rather than rendered blank).
 */
export const mapTriageMetadataToNoteProps = (
  meta: TriageAgentMetadata,
): AITriageNoteProps => {
  const { user_info, ticket_metadata, ticket_summary, edited_ticket_fields } =
    meta;

  const details: DetailRow[] = [];

  const userName = user_info.username ?? user_info.customer_name;
  if (userName) details.push({ label: "User", value: userName });
  if (ticket_metadata.impact)
    details.push({ label: "Impact", value: capitalize(ticket_metadata.impact) });
  if (ticket_metadata.urgency)
    details.push({
      label: "Urgency",
      value: capitalize(ticket_metadata.urgency),
    });
  const priority = edited_ticket_fields.priority || ticket_metadata.priority;
  if (priority)
    details.push({ label: "Priority", value: capitalize(priority) });
  if (edited_ticket_fields.category) {
    const category = edited_ticket_fields.subcategory
      ? `${edited_ticket_fields.category} → ${edited_ticket_fields.subcategory}`
      : edited_ticket_fields.category;
    details.push({ label: "Category", value: category });
  }
  details.push({
    label: "Screenshot",
    value: meta.screenshot_attached ? "Yes" : "No",
  });

  const tagCount = edited_ticket_fields.tags?.length ?? 0;

  return {
    enrichedSubject: ticket_summary.enriched_subject || undefined,
    enrichedDescription: ticket_summary.enriched_description || undefined,
    details,
    checklist: ticket_summary.clarification_outcome ?? [],
    agentActions: meta.agent_actions ?? [],
    sentiment: meta.user_sentiment
      ? `Sentiment: ${capitalize(meta.user_sentiment)}`
      : null,
    // No contact number or explicit start-time in the payload — omit rows.
    contactInfo: null,
    startedFrom: null,
    routing: {
      board: edited_ticket_fields.board?.trim() || undefined,
      status: edited_ticket_fields.status || undefined,
      tagCount,
    },
    interruptedNote: buildInterruptedNote(meta),
  };
};
